import { FileText, Paperclip } from "lucide-react";
import { lazy, Suspense, useState, type JSX } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@mailflare/ui";
import type { Attachment } from "@mailflare/shared";

import { formatBytes } from "@/lib/utils";

const AttachmentPreview = lazy(() => import("./attachment-preview"));

export interface AttachmentListProps {
  readonly attachments: readonly Attachment[];
}

/** Attachment chips with a lazily loaded inline preview dialog. */
export function AttachmentList({ attachments }: AttachmentListProps): JSX.Element | null {
  const [preview, setPreview] = useState<Attachment | null>(null);

  if (attachments.length === 0) return null;

  return (
    <section aria-label="Attachments" className="border-t pt-3">
      <p className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
        <Paperclip aria-hidden className="size-3.5" />
        {attachments.length} attachment{attachments.length === 1 ? "" : "s"}
      </p>
      <ul className="flex flex-wrap gap-2">
        {attachments.map((attachment) => (
          <li key={attachment.id}>
            <button
              type="button"
              onClick={() => setPreview(attachment)}
              aria-label={`Preview ${attachment.filename}, ${formatBytes(attachment.sizeBytes)}`}
              className="border-border hover:bg-accent focus-visible:ring-ring flex max-w-64 items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs focus-visible:ring-2 focus-visible:outline-none"
            >
              <FileText aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{attachment.filename}</span>
                <span className="text-muted-foreground">{formatBytes(attachment.sizeBytes)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <Dialog
        open={preview !== null}
        onOpenChange={(open) => (!open ? setPreview(null) : undefined)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate">{preview?.filename ?? "Attachment"}</DialogTitle>
            <DialogDescription className="sr-only">Attachment preview</DialogDescription>
          </DialogHeader>
          {preview !== null ? (
            <Suspense fallback={<Spinner label="Loading preview" />}>
              <AttachmentPreview attachment={preview} />
            </Suspense>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
